/**
 * Кнопка-молния у поля ответа «Чатов»: заготовленные сообщения, которые собираются сами.
 *
 * В отличие от шаблонов (готовый текст из справочника) заготовка подставляет то, что есть
 * только у этого диалога: ссылку клиента на самозапись `my.uni-renter.ru/z/<хэш>`
 * (07.10.26, замечание владельца: клиент назвал день без часа — нужно одним нажатием дать ему
 * «как определитесь со временем, запишитесь по ссылке») и ссылки на ботов Telegram и MAX для
 * клиента, который хочет сдать платье, — их параметр `start` привязывает чат бота к этому чату.
 * Заготовка «сдать платье» — под канал чата (владелец, 07.10.26): в Telegram зовём только в бот
 * Telegram отметкой @unirenterbot, в MAX — только в бот MAX, клиенту, который уже пишет боту, —
 * сразу список «что прислать», в WhatsApp её нет.
 * «Страница брони» (22.10.26) — ссылка клиента на `my.uni-renter.ru/d/<хэш>`: даты, платья, оплата
 * и повторная примерка. «Повторная примерка» (22.10.26) — посадочная той же брони `/d/<хэш>/refit`:
 * записи нет — клиент сразу записывается (заявку подтверждает руководитель салона), есть —
 * изменить, отменить или оставить. «Доставка курьером» (22.10.26) — та же страница брони на строке
 * «Получение»: переключатель «В салоне | Курьером», адрес и окно доставки и забора.
 * «QR на оплату» (22.10.26) — итог по сделке и QR-код СБП файлом: счёт заводит CRM (`sPayment`,
 * QR-код), сумма по умолчанию — «к оплате», её можно поменять (предоплата).
 * Клик по заготовке выдаёт ссылку и кладёт текст в поле ответа: клиенту он уйдёт, когда
 * сотрудник нажмёт «Отправить».
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QrCode, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  dealPageLinkDraft,
  paymentQrCreate,
  paymentQrForm,
  sdatLinkDraft,
  type ZapisDraft,
  zapisLinkDraft,
} from "@/lib/dealStage";

const rub = (v: number) => `${v.toLocaleString("ru-RU")} ₽`;

/** Итог по сделке, сумма и «Создать QR»: текст — в поле ответа, JPEG — во вложения. */
function PaymentQr({ chatId, onDone }: { chatId: number; onDone: (text: string, file: File) => void }) {
  const qc = useQueryClient();
  const form = useQuery({ queryKey: ["payment-qr", chatId], queryFn: () => paymentQrForm(chatId), staleTime: 0 });
  const [amount, setAmount] = useState("");
  const due = form.data?.money.due ?? 0;
  useEffect(() => {
    if (form.data) setAmount(due > 0 ? String(due) : "");
  }, [form.data, due]);
  const create = useMutation({
    mutationFn: () => paymentQrCreate(chatId, Number(amount)),
    onSuccess: (r) => {
      const bytes = Uint8Array.from(atob(r.qr), (c) => c.charCodeAt(0));
      const ext = r.qr_type === "image/png" ? "png" : "jpg";
      onDone(r.text, new File([bytes], `qr-oplata-${r.payment_id}.${ext}`, { type: r.qr_type }));
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] }),
  });
  if (form.isPending) return <p className="px-3 py-1.5 text-[13px] text-[var(--tg-sec)]">Считаю сумму по сделке…</p>;
  if (form.isError)
    return (
      <p role="alert" className="px-3 py-1.5 text-[13px] text-[#e53935]">
        {form.error.message}
      </p>
    );
  const m = form.data.money;
  const ok = Number(amount) > 0 && form.data.qr;
  return (
    <div className="px-3 py-1.5" data-annot="QR на оплату">
      <div className="text-[13px] text-[var(--tg-sec)]">
        {m.total ? `Итого ${rub(m.total)} · оплачено ${rub(m.paid)} · к оплате ${rub(m.due)}` : "Договора ещё нет — сумму введите сами"}
      </div>
      {form.data.qr ? (
        <form
          className="mt-1.5 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (ok) create.mutate();
          }}
        >
          <input
            type="number"
            min={1}
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-32 rounded-lg border border-[var(--tg-border,#ccc)] bg-transparent px-2 py-1 text-[14px]"
            aria-label="Сумма к оплате, ₽"
            autoFocus
          />
          <span className="text-[var(--tg-sec)]">₽</span>
          <button
            type="submit"
            disabled={!ok || create.isPending}
            className="ml-auto rounded-full bg-[var(--tg-primary)] px-3 py-1 text-[13px] font-medium text-white disabled:opacity-50"
          >
            {create.isPending ? "Создаю счёт…" : "Создать QR"}
          </button>
        </form>
      ) : (
        <p className="mt-1 text-[13px] text-[#e53935]">У салона сделки ({form.data.city ?? "не указан"}) нет QR-кассы</p>
      )}
      {create.isError && (
        <p role="alert" className="mt-1 text-[13px] text-[#e53935]">
          {create.error.message}
        </p>
      )}
    </div>
  );
}

type Kind = ZapisDraft | "sdat" | "deal" | "refit" | "delivery";
type Quick = { kind: Kind; title: string; text: string };

const SDAT = "Чтобы стилисты салона посмотрели ваше платье";

/** «Хочет сдать платье» под канал чата; `null` — в этом канале заготовки нет (WhatsApp). */
function sdatQuick(channel: string, web: boolean): Quick | null {
  if (channel === "whatsapp") return null;
  if ((channel === "telegram" || channel === "max") && !web)
    return {
      kind: "sdat",
      title: "Хочет сдать платье — что прислать боту",
      text: `${SDAT}, пришлите, пожалуйста, сюда: номер телефона, фото платья, бренд…`,
    };
  if (channel === "telegram")
    return {
      kind: "sdat",
      title: "Хочет сдать платье — бот Telegram",
      text: `${SDAT}, напишите, пожалуйста, нашему боту @unirenterbot — он подскажет, что прислать.`,
    };
  if (channel === "max")
    return {
      kind: "sdat",
      title: "Хочет сдать платье — ссылка на бота MAX",
      text: `${SDAT}, напишите, пожалуйста, нашему боту в MAX — он подскажет, что прислать: ‹ссылка›`,
    };
  return {
    kind: "sdat",
    title: "Хочет сдать платье — ссылки на бота Telegram и MAX",
    text: `${SDAT}, напишите нам в Telegram или MAX — бот подскажет, что прислать: ‹ссылки›`,
  };
}

/** Что в списке: `text` — как прочтёт клиент, вместо ссылки — пометка (её выдаёт сервер). */
const QUICK: Quick[] = [
  {
    kind: "later",
    title: "Определится со временем — ссылка на запись",
    text: "Как определитесь со временем, вы можете записаться по этой ссылке: ‹ссылка›. Будем вас ждать!",
  },
  {
    kind: "invite",
    title: "Выбрать день и время — ссылка на запись",
    text: "Выбрать удобный день и время примерки можно самостоятельно по ссылке — там же видно, когда в салоне свободнее: ‹ссылка›",
  },
  {
    kind: "deal",
    title: "Страница брони — даты, платья, повторная примерка",
    text: "Всё о вашей брони — даты проката, платья, оплата и повторная примерка — на вашей странице: ‹ссылка›",
  },
  {
    kind: "refit",
    title: "Повторная примерка — запись по ссылке",
    text: "Записаться на повторную примерку или изменить запись можно здесь: ‹ссылка›",
  },
  {
    kind: "delivery",
    title: "Доставка курьером — заказать на странице брони",
    text: "Заказать доставку курьером к началу проката и забор в конце можно на странице вашей брони: в блоке «Прокат» выберите «Курьером» и укажите адрес и удобное время — ‹ссылка›",
  },
];

export function QuickButton({
  chatId,
  channel,
  web,
  onPick,
  onFiles,
}: {
  chatId: number;
  /** Канал чата и «чат нашего номера из браузера» (`web:…`) — под них собирается «сдать платье». */
  channel: string;
  web: boolean;
  onPick: (text: string) => void;
  /** Файлы заготовки (QR на оплату) — во вложения ответа. */
  onFiles: (files: File[]) => void;
}) {
  const qc = useQueryClient();
  const sdat = sdatQuick(channel, web);
  const list = sdat ? [...QUICK, sdat] : QUICK;
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const make = useMutation({
    mutationFn: (kind: Kind): Promise<{ text: string }> =>
      kind === "sdat"
        ? sdatLinkDraft(chatId)
        : kind === "deal" || kind === "refit" || kind === "delivery"
          ? dealPageLinkDraft(chatId, kind)
          : zapisLinkDraft(chatId, kind),
    onSuccess: (r) => {
      onPick(r.text);
      setOpen(false);
    },
    // Выдача ссылки — действие в журнале панели, в том числе отбитое ошибкой.
    onSettled: () => void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] }),
  });
  const reset = make.reset;

  // Клик мимо списка и Escape закрывают его; ошибка прошлой попытки с ним не переживает.
  useEffect(() => {
    if (!open) return;
    reset();
    setQr(false);
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open, reset]);

  return (
    <div ref={box} className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Заготовки: сообщение со ссылкой на запись, на страницу брони или на бота — в поле ответа"
        aria-expanded={open}
        className={`grid size-12 place-items-center rounded-full bg-[var(--tg-surface)] shadow-[0_1px_8px_1px_rgba(0,0,0,.12)] hover:bg-[var(--tg-hover)] ${
          open ? "text-[var(--tg-primary)]" : "text-[var(--tg-sec)]"
        }`}
        data-annot="заготовки ответа"
      >
        <Zap size={22} />
      </button>
      {open && (
        <div
          className="absolute bottom-14 right-0 z-30 flex w-[min(420px,calc(100vw-16px))] flex-col overflow-hidden rounded-[16px] bg-[var(--tg-surface)] py-1 text-[14px] shadow-[0_4px_24px_rgba(0,0,0,.28)]"
          data-annot="список заготовок"
        >
          <div className="px-3 pb-0.5 pt-2 text-[12px] font-medium uppercase tracking-wide text-[var(--tg-sec)]">
            Заготовки — текст встанет в поле ответа
          </div>
          {list.map((q) => (
            <button
              key={q.kind}
              type="button"
              disabled={make.isPending}
              onClick={() => make.mutate(q.kind)}
              className="block w-full px-3 py-1.5 text-left hover:bg-[var(--tg-hover)] disabled:opacity-60"
            >
              <div className="font-medium">{make.isPending && make.variables === q.kind ? "Готовлю ссылку…" : q.title}</div>
              <div className="text-[13px] text-[var(--tg-sec)]">{q.text}</div>
            </button>
          ))}
          <button
            type="button"
            onClick={() => setQr((v) => !v)}
            aria-expanded={qr}
            className="block w-full border-t border-[var(--tg-hover)] px-3 py-1.5 text-left hover:bg-[var(--tg-hover)]"
          >
            <div className="flex items-center gap-1.5 font-medium">
              <QrCode size={16} /> QR на оплату — сумма по сделке и QR-код файлом
            </div>
            <div className="text-[13px] text-[var(--tg-sec)]">
              Сумма заказа, оплачено, осталось к оплате; QR-код для оплаты через СБП — во вложении. Счёт заведётся в CRM.
            </div>
          </button>
          {qr && (
            <PaymentQr
              chatId={chatId}
              onDone={(value, file) => {
                onPick(value);
                onFiles([file]);
                setOpen(false);
              }}
            />
          )}
          {make.isError && (
            <p role="alert" className="px-3 py-1.5 text-[13px] text-[#e53935]">
              {make.error.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
